import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Typography, Spin, Alert, List, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import axios from '../../utils/axios';

const { Text } = Typography;

const AffectedContactsModal = ({ visible, onCancel, contactIds, title = 'Affected Customers' }) => {
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);

  const fetchContactDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the new batch contact details API for better performance
      const response = await axios.post('/timeline/contacts/batch-details', contactIds);
      
      if (response.data.success) {
        const contactDetails = response.data.contacts.map(contact => ({
          id: contact.id,
          email: contact.email,
          firstName: contact.first_name,
          lastName: contact.last_name,
          phone: contact.phone,
          hasData: true
        }));
        setContacts(contactDetails);
      } else {
        throw new Error('Failed to fetch contact details');
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
      // Fallback to individual API calls if batch fails
      try {
        const contactPromises = contactIds.map(async (contactId) => {
          try {
            const response = await axios.get(`/timeline/contact/${contactId}/details`);
            return {
              id: contactId,
              email: response.data.email,
              firstName: response.data.first_name,
              lastName: response.data.last_name,
              phone: response.data.phone,
              hasData: true
            };
          } catch (error) {
            console.warn(`Failed to fetch contact ${contactId}:`, error);
            return {
              id: contactId,
              email: null,
              firstName: null,
              lastName: null,
              name: `Contact ${contactId}`,
              hasData: false
            };
          }
        });

        const contactDetails = await Promise.all(contactPromises);
        setContacts(contactDetails);
      } catch (fallbackError) {
        console.error('Fallback contact fetch also failed:', fallbackError);
        setError('Failed to load contact details');
      }
    } finally {
      setLoading(false);
    }
  }, [contactIds]);

  useEffect(() => {
    if (visible && contactIds && contactIds.length > 0) {
      fetchContactDetails();
    }
  }, [visible, contactIds, fetchContactDetails]);

  const formatContactName = (contact) => {
    // Prefer first_name + last_name if available
    if (contact.firstName || contact.lastName) {
      const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
      if (name.trim()) {
        return name;
      }
    }
    
    // Fallback to extracting name from email
    if (contact.email) {
      const emailParts = contact.email.split('@');
      const localPart = emailParts[0];
      // Try to format name from email local part
      const formattedName = localPart
        .replace(/[._-]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      return formattedName;
    }
    
    // Final fallback
    return contact.name || `Contact ${contact.id}`;
  };

  const formatContactSubtitle = (contact) => {
    if (contact.email) {
      return contact.email;
    }
    return `ID: ${contact.id}`;
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <div>
        <Text>Contacts affected by this analysis:</Text>
        <div style={{ marginTop: '12px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '12px' }}>
                <Text type="secondary">Loading contact details...</Text>
              </div>
            </div>
          ) : error ? (
            <Alert message={error} type="error" />
          ) : contacts.length === 0 ? (
            <Text type="secondary">No contacts found</Text>
          ) : (
            <List
              dataSource={contacts}
              renderItem={(contact, index) => (
                <List.Item
                  key={contact.id}
                  style={{ 
                    borderBottom: index === contacts.length - 1 ? 'none' : '1px solid #f0f0f0',
                    padding: '12px 0'
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        icon={<UserOutlined />} 
                        style={{ 
                          backgroundColor: contact.hasData ? '#1890ff' : '#d9d9d9',
                          color: 'white'
                        }}
                      />
                    }
                    title={
                      <a 
                        href={`/timeline-list?contact=${encodeURIComponent(contact.id.toString())}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          color: '#1890ff',
                          textDecoration: 'none',
                          fontWeight: 500
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {formatContactName(contact)}
                      </a>
                    }
                    description={
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {formatContactSubtitle(contact)}
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AffectedContactsModal; 